import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { PortfolioMetadataApiModel, TargetingListApi } from '../../../api/targeting/model/targeting-list-api.model';
import { PortfolioCategory } from '../enums/portfolio-category.enum';
import { PortfolioRegion } from '../enums/portfolio-region.enum';
import { AttributeModel } from '../../../Common/Misc/models/attribute.model';
import { attributeTypesEnum } from '../../../Common/Misc/enums/attribute-types.enum';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { SortFieldEnum } from '../../../Common/TargetingShared/enum/SortField.enum';
import { TargetingListTypeEnum } from '../enums/targeting-list-type.enum';
import { BaseModel } from '../../../Common/Basic/models/base.model';


// @TODO - double check which properties should be optional
export class TargetingList extends BaseModel<TargetingList, TargetingListApi> implements AuditableModel {
    id?: number;
    name?: string;

    isFavorite: boolean;

    attributes?: AttributeModel[];
    hidden?: boolean;
    listType?: TargetingListTypeEnum;
    groupId?: number;

    portfolioMetadata?: PortfolioMetadataModel;

    createdByUserId?: number;
    createdDate?: Date;

    modifiedByUserId?: number;
    modifiedDate?: Date;

    constructor(apiModel?: TargetingListApi) {
        super();

        if (apiModel) {
            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }
    }

    constructFromApiModel(apiModel: TargetingListApi) {
        this.id = apiModel.id;
        this.name = apiModel.name;

        this.hidden = apiModel.hidden;
        this.listType = (apiModel.listType) ? apiModel.listType : TargetingListTypeEnum.NORMAL;

        this.groupId = apiModel.displayGroupId;
        this.isFavorite = apiModel.isFavorited;

        this.attributes = [
            {
                label: 'Discover.targeting.panels.createdUser',
                id: 0,
                value: apiModel.createdUserId,
                type: attributeTypesEnum.user
            },
            {
                label: 'Discover.targeting.panels.createdDate',
                id: 1,
                value: apiModel.createdDate,
                type: attributeTypesEnum.date
            },
            {
                label: 'Discover.targeting.panels.modifiedUser',
                id: 2,
                value: apiModel.modifiedUserId,
                type: attributeTypesEnum.user
            },
            {
                label: 'Discover.targeting.panels.modifiedDate',
                id: 3,
                value: apiModel.modifiedDate,
                type: attributeTypesEnum.date
            }
        ].filter(x => !!x.value);

        if (apiModel.portfolioMetadata) {
            this.portfolioMetadata = new PortfolioMetadataModel(apiModel.portfolioMetadata);
        }

        this.createdByUserId = apiModel.createdUserId;
        this.createdDate = apiModel.createdDate;

        this.modifiedByUserId = apiModel.modifiedUserId;
        this.modifiedDate = apiModel.modifiedDate;
    }

    constructDefault() {
        this.portfolioMetadata = new PortfolioMetadataModel();
        this.isFavorite = false;
    }

    toApiModel(): TargetingListApi {
        return {
            id:                        this.id,
            name:                      this.name,
            portfolioMetadata:         !!this.portfolioMetadata ? this.portfolioMetadata.toApiModel() : null,
            hidden:                    this.hidden,
            listType:                  this.listType,
            displayGroupId:            this.groupId,
            isFavorited:               this.isFavorite
        };
    }

    clone(): TargetingList {
        return new TargetingList(this.toApiModel());
    }

    /** Doesn't compare {@link attributes} since its covered in the create/modified comparisons */
    equals(other?: TargetingList): boolean {
        if (!other) {
            return false;
        }

        if (this.id !== other.id) {
            return false;
        }

        if (this.name !== other.name) {
            return false;
        }

        if (this.isFavorite !== other.isFavorite) {
            return false;
        }

        if (this.hidden !== other.hidden) {
            return false;
        }

        if (this.listType !== other.listType) {
            return false;
        }

        if (this.groupId !== other.groupId) {
            return false;
        }

        if (!this.portfolioMetadata.equals(other.portfolioMetadata)) {
            return false;
        }


        if (this.createdByUserId !== other.createdByUserId) {
            return false;
        }

        if (this.createdDate !== other.createdDate) {
            return false;
        }

        if (this.modifiedByUserId !== other.modifiedByUserId) {
            return false;
        }

        if (this.modifiedDate !== other.modifiedDate) {
            return false;
        }

        return true;
    }
}

export class PortfolioMetadataModel {
    active: boolean;
    entityType: EntityType;
    region: PortfolioRegion;
    category: PortfolioCategory;
    defaultSortField: SortFieldEnum;

    frCAName: string;
    frFRName: string;
    enUSName: string;
    enGBName: string;

    constructor(apiModel?: PortfolioMetadataApiModel) {
        if (apiModel) {
            apiModel.displayNames.forEach(x => {
                switch (x.locale) {
                    case 'en-US': this.enUSName = x.value; break;
                    case 'en-GB': this.enGBName = x.value; break;
                    case 'fr-FR': this.frFRName = x.value; break;
                    case 'fr-CA': this.frCAName = x.value; break;
                }
            });
            this.active = apiModel.active;
            this.entityType = apiModel.entityType;
            this.region = apiModel.region;
            this.category = apiModel.category;
            this.defaultSortField = apiModel.defaultSortField;
        }
    }

    toApiModel(): PortfolioMetadataApiModel {
        return {
            displayNames: [
                { locale: 'en-US', value: this.enUSName},
                { locale: 'en-GB', value: this.enGBName},
                { locale: 'fr-CA', value: this.frCAName},
                { locale: 'fr-FR', value: this.frFRName},
            ],
            active: this.active,
            region: this.region,
            entityType: this.entityType,
            category: this.category,
            defaultSortField: this.defaultSortField
        };
    }

    equals(other?: PortfolioMetadataModel): boolean {
        if (!other) {
            return false;
        }

        if (this.active !== other.active) {
            return false;
        }

        if (this.entityType !== other.entityType) {
            return false;
        }

        if (this.region !== other.region) {
            return false;
        }

        if (this.category !== other.category) {
            return false;
        }

        if (this.defaultSortField !== other.defaultSortField) {
            return false;
        }

        if (this.frCAName !== other.frCAName) {
            return false;
        }

        if (this.frFRName !== other.frFRName) {
            return false;
        }

        if (this.enUSName !== other.enUSName) {
            return false;
        }

        if (this.enGBName !== other.enGBName) {
            return false;
        }

        return true;
    }
}
